import React, { Component } from "react";
import CartContext from "./CartContext";
import Swal from "sweetalert2";
import { Form, Button, Row, Col, Table } from "react-bootstrap";

export class LoyaltyPointConfig extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      slabs: [], // array of { minPurchaseAmount: '', pointsToReward: '' }
      loading: false,
      saving: false,
    };
  }

  componentDidMount() {
    this.LoadConfig();
  }

  // Load existing config (all slabs) and keep them in state
  LoadConfig = () => {
    const api = process.env.REACT_APP_API;
    const url = api + "LoyaltyConfig/configs/" + this.context.storeId;

    this.setState({ loading: true });

    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        // expect an array. normalize to slabs
        if (Array.isArray(data)) {
          const slabs = data.map((d) => ({
            minPurchaseAmount: d.minPurchaseAmount?.toString() || "",
            pointsToReward: d.pointsToReward?.toString() || "",
          }));
          // sort ascending for admin convenience
          slabs.sort(
            (a, b) => Number(a.minPurchaseAmount) - Number(b.minPurchaseAmount)
          );
          this.setState({ slabs, loading: false });
        } else {
          this.setState({ slabs: [], loading: false });
        }
      })
      .catch((err) => {
        this.setState({ loading: false });
        Swal.fire(
          "Error",
          "Failed to load configuration: " + (err.message || err),
          "error"
        );
      });
  };

  onAddSlab = () => {
    this.setState((prev) => ({
      slabs: [...prev.slabs, { minPurchaseAmount: "", pointsToReward: "" }],
    }));
  };

  onChangeSlab = (idx, field, value) => {
    const slabs = [...this.state.slabs];
    slabs[idx] = { ...slabs[idx], [field]: value };
    this.setState({ slabs });
  };

  onRemoveSlab = (idx) => {
    const slab = this.state.slabs[idx];
    // If slab has an existing numeric minPurchaseAmount, call delete API
    if (
      slab &&
      slab.minPurchaseAmount &&
      slab.minPurchaseAmount.toString().trim() !== ""
    ) {
      Swal.fire({
        title: "Delete slab?",
        text: `Delete slab with min amount ${slab.minPurchaseAmount}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete",
      }).then((r) => {
        if (r.isConfirmed) this.DeleteSlab(idx);
      });
    } else {
      // just remove unsaved row
      this.setState((prev) => ({
        slabs: prev.slabs.filter((_, i) => i !== idx),
      }));
    }
  };

  DeleteSlab = (idx) => {
    const slab = this.state.slabs[idx];
    const api = process.env.REACT_APP_API;
    const url = `${api}LoyaltyConfig/Delete?storeId=${
      this.context.storeId
    }&minAmount=${encodeURIComponent(slab.minPurchaseAmount)}`;

    this.setState({ loading: true });
    fetch(url, { method: "DELETE" })
      .then((res) => {
        this.setState({ loading: false });
        if (res.ok) {
          Swal.fire("Deleted", "Slab deleted.", "success");
          this.LoadConfig();
        } else {
          Swal.fire("Error", "Failed to delete slab.", "error");
        }
      })
      .catch((err) => {
        this.setState({ loading: false });
        Swal.fire("Error", "Delete failed: " + (err.message || err), "error");
      });
  };

  // Save all slabs (create or update each row). Calls API per slab.
  onSaveAll = async () => {
    // basic validation: ensure no empty fields and numeric
    const { slabs } = this.state;
    if (!slabs || slabs.length === 0) {
      Swal.fire(
        "Validation",
        "Add at least one slab before saving.",
        "warning"
      );
      return;
    }

    for (let i = 0; i < slabs.length; i++) {
      const s = slabs[i];
      if (s.minPurchaseAmount === "" || s.pointsToReward === "") {
        Swal.fire(
          "Validation",
          `Please fill both fields for row ${i + 1}`,
          "warning"
        );
        return;
      }
      if (
        isNaN(Number(s.minPurchaseAmount)) ||
        isNaN(Number(s.pointsToReward))
      ) {
        Swal.fire(
          "Validation",
          `Numeric values required for row ${i + 1}`,
          "warning"
        );
        return;
      }
      if (Number(s.minPurchaseAmount) < 0 || Number(s.pointsToReward) < 0) {
        Swal.fire(
          "Validation",
          `Values must be non-negative for row ${i + 1}`,
          "warning"
        );
        return;
      }
    }

    this.setState({ saving: true });

    const api = process.env.REACT_APP_API;

    try {
      // Save each slab (sequentially to avoid DB race for same min amount)
      for (const s of slabs) {
        const payload = {
          MinPurchaseAmount: parseFloat(s.minPurchaseAmount),
          PointsToReward: parseFloat(s.pointsToReward),
        };

        const url =
          api +
          "LoyaltyConfig/CreateOrUpdateConfiguration/" +
          this.context.storeId;
        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(
            `Failed to save slab min=${s.minPurchaseAmount}: ${
              text || res.status
            }`
          );
        }
      }

      this.setState({ saving: false });
      Swal.fire("Success", "All slabs saved successfully.", "success");
      this.LoadConfig();
    } catch (err) {
      this.setState({ saving: false });
      Swal.fire("Error", "Save failed: " + (err.message || err), "error");
    }
  };

  render() {
    return (
      <div className="p-3 bg-white rounded shadow-sm">
        <h4>
          <b>Loyalty Point Configuration (multiple slabs)</b>
        </h4>

        <div className="mt-3">
          <div className="mb-2 d-flex justify-content-between align-items-center">
            <div>
              <small className="text-muted">
                Define one or more slabs. Admin UI will save each slab
                separately.
              </small>
            </div>
            <div>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={this.onAddSlab}
                className="me-2"
              >
                Add Slab
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={this.onSaveAll}
                disabled={this.state.saving}
              >
                {this.state.saving ? "Saving..." : "Save All"}
              </Button>
            </div>
          </div>

          <Table bordered hover size="sm">
            <thead>
              <tr>
                <th style={{ width: "40%" }}>Min Purchase Amount</th>
                <th style={{ width: "40%" }}>Points To Reward</th>
                <th style={{ width: "20%" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {this.state.slabs && this.state.slabs.length > 0 ? (
                this.state.slabs.map((s, idx) => (
                  <tr key={idx}>
                    <td>
                      <Form.Control
                        type="number"
                        min={0}
                        value={s.minPurchaseAmount}
                        onChange={(e) =>
                          this.onChangeSlab(
                            idx,
                            "minPurchaseAmount",
                            e.target.value
                          )
                        }
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        min={0}
                        value={s.pointsToReward}
                        onChange={(e) =>
                          this.onChangeSlab(
                            idx,
                            "pointsToReward",
                            e.target.value
                          )
                        }
                      />
                    </td>
                    <td className="text-center">
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => this.onRemoveSlab(idx)}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="text-center text-muted">
                    No slabs defined. Click "Add Slab" to create.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
    );
  }
}

export default LoyaltyPointConfig;
